import * as React from 'react';
import { Banner, Flex, FlexItem } from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { useApiHealthStore } from '@app/store/apiHealthStore';
import { Truncation, useDataCompletenessStore } from '@app/store/dataCompletenessStore';

/**
 * Counting seventeen collections is cheap, but it is still seventeen requests, and at startup they
 * would be competing for the browser's handful of connections with the data the user is actually
 * waiting for. Nothing here is urgent — a collection that outgrew its size did so hours ago — so it
 * waits for the first screen to finish loading.
 */
const CHECK_DELAY_MS = 5000;

/** "Holidays (3,201 records, 1,000 loaded)" */
const describe = (t: Truncation): string => {
  const total = t.total === null ? `more than ${t.size.toLocaleString()}` : t.total.toLocaleString();
  return `${t.label} (${total} records, ${t.size.toLocaleString()} loaded)`;
};

/**
 * Warns that a collection has outgrown the single request its store loads it with, so the pages
 * built on it are showing an incomplete list. See store/dataCompletenessStore.ts for the check.
 *
 * A banner rather than a modal or a toast, for the same reason ApiHealthBanner is one: this is a
 * standing condition, not an event. It cannot be fixed from the browser and it will still be true
 * in an hour, so a dialog would be dismissed once and the data would go on being wrong silently
 * for the rest of the session. The banner stays for exactly as long as the problem does.
 */
const DataCompletenessBanner: React.FunctionComponent = () => {
  const truncations = useDataCompletenessStore((state) => state.truncations);
  const hasRun = useDataCompletenessStore((state) => state.hasRun);
  const check = useDataCompletenessStore((state) => state.check);

  // Bumped when the service comes back. A check that ran during an outage measured nothing, and
  // this is when it can finally get an answer.
  const retryToken = useApiHealthStore((state) => state.retryToken);

  // Read inside the effect without making it re-run when the check completes.
  const hasRunRef = React.useRef(hasRun);
  hasRunRef.current = hasRun;

  React.useEffect(() => {
    if (hasRunRef.current) return;

    const timer = window.setTimeout(() => void check(), CHECK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [check, retryToken]);

  if (truncations.length === 0) return null;

  const one = truncations.length === 1;

  // screenReaderText carries what the red conveys visually, so it must not repeat the message
  // below it — a screen reader would otherwise read "Incomplete data" twice.
  return (
    <Banner status="danger" isSticky screenReaderText="Error">
      <Flex
        alignItems={{ default: 'alignItemsCenter' }}
        spaceItems={{ default: 'spaceItemsSm' }}
        flexWrap={{ default: 'nowrap' }}
      >
        <FlexItem>
          <ExclamationCircleIcon />
        </FlexItem>
        {/* Banner is built for one short line and sets white-space: nowrap, which would cut this
            off mid-sentence — and the part that gets cut is the part that says what to do. */}
        <FlexItem style={{ whiteSpace: 'normal' }}>
          <strong>Incomplete data.</strong> {truncations.map(describe).join(', ')} — more records
          exist than this application loads, so {one ? 'that list is' : 'those lists are'} missing
          rows and {one ? 'its' : 'their'} filters and counts are wrong. Please report this to your
          administrator.
        </FlexItem>
      </Flex>
    </Banner>
  );
};

export { DataCompletenessBanner };
